import React, { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import {
    PRE_INSET_TIMEOUT_MS,
    SOFT_KEYBOARD_MIN_PX,
    estimatedKeyboardInset,
    isIOS,
    nativeKeyboard,
    rememberKeyboardInset,
    useNativeKeyboardHeight,
    useViewportInset,
} from './keyboardShared';

/**
 * 시트(아래에서 올라오는 창) 안 입력칸의 화면 키보드 처리 — 대화방 시트에서 시작해 공용 SheetDialog·FilterPanel 에서도 쓴다.
 *
 * 쓰는 법:
 * - `const keyboard = useSheetKeyboard(open && mobile)` — 시트가 열려 있고 폰 화면일 때만 켠다.
 * - `keyboard.containerProps` 는 시트 내용을 감싼 칸(div)에 펼쳐 단다 — 누르는 순간(손을 뗄 때) 처리와 ref.
 * - `keyboard.preInset` 은 공용 시트(FilterPanel)의 `keyboardPreInset` 으로 넘긴다 — 잰 키보드 높이가 없을 때만 쓰인다.
 * - `keyboard.inset` 은 시트 아래 흰 바탕 높이(키보드가 가린 높이, 미리 올린 자리 포함)로 쓴다.
 *
 * 전제: 포커스가 "이 시트인가"는 containerProps 를 단 칸에서 가장 가까운 `[role="dialog"]` 안에 포커스가 있는지로 판단한다 —
 * 시트는 role="dialog" 요소 안에 그려져야 한다.
 */

/** 기본 폰 폭 기준 — 대화방(THREAD_MOBILE_MAX_WIDTH_PX)·공용 필터(FILTER_MOBILE_MAX_WIDTH_PX)와 같은 값 */
const SHEET_MOBILE_MAX_WIDTH_PX: number = 768;

const isMobileWidth = (maxWidthPx: number): boolean => window.matchMedia(`(max-width: ${maxWidthPx}px)`).matches;

/** 시트 안에서 키보드를 띄우는 칸(글자 입력) — 체크·라디오·읽기 전용(달력 열기 칸 등)은 뺀다 */
const keyboardFieldOf = (target: EventTarget | null): HTMLInputElement | HTMLTextAreaElement | null => {
    const node: Element | null = target instanceof Element ? target : null;
    const field: Element | null = node?.closest('textarea, input') ?? null;
    if (!(field instanceof HTMLTextAreaElement) && !(field instanceof HTMLInputElement)) return null;
    if (field.readOnly || field.disabled) return null;
    if (field instanceof HTMLInputElement && ['checkbox', 'radio', 'button', 'submit', 'range', 'file', 'hidden'].includes(field.type)) return null;
    return field;
};

export interface SheetKeyboard {
    /** 공용 시트(FilterPanel)의 keyboardPreInset 로 넘길 값 — 잰 키보드 높이가 없을 때만 쓰인다 */
    preInset: number | undefined;
    /** 지금 키보드(미리 올린 자리 포함)가 가린 높이 — 시트 아래 흰 바탕 높이 */
    inset: number;
    /** 시트 내용을 감싼 칸에 단다(누르는 순간 처리) */
    containerProps: {
        ref: React.RefObject<HTMLDivElement>;
        onTouchStartCapture: (event: React.TouchEvent<HTMLDivElement>) => void;
        onTouchEndCapture: (event: React.TouchEvent<HTMLDivElement>) => void;
    };
}

export interface SheetKeyboardOptions {
    /** 이 폭 이하일 때만 누르는 순간 처리를 한다(기본 768 — 대화방과 같음) */
    mobileMaxWidthPx?: number;
}

/**
 * 대화방 시트(이의 제기·면접 시간 요청·합격 메모 등)의 화면 키보드 처리 — 아래 입력줄(useComposerKeyboard)과 같은 원칙(UI 가이드 7.1).
 * - 아이폰에서 칸을 누르는 순간(손을 뗄 때) 시트를 키보드 자리로 먼저 올린 뒤 직접 포커스를 준다 — 그냥 두면 칸이 키보드에 가릴 자리라
 *   iOS 가 화면 전체(뒤 머리줄·시트)를 밀어 올렸다(2026-10-06 시뮬레이터).
 * - 새 앱(키보드 플러그인): 입력하는 동안 화면 줄이기를 끄고 keyboardWillShow 높이로 시트를 올린다 — 화면 줄이기에 맡기면 키보드가
 *   다 올라온 뒤에야 시트가 따라 올라왔다. 키보드가 다 내려가면 되돌린다.
 * - 키보드가 떠 있는 동안 끌 곳이 없는 끌기(시트 머리·버튼 줄·어두운 바탕)는 막는다 — iOS 가 보이는 화면을 끌어 시트가 흔들렸다.
 */
export const useSheetKeyboard = (active: boolean, options?: SheetKeyboardOptions): SheetKeyboard => {
    const mobileMaxWidthPx: number = options?.mobileMaxWidthPx ?? SHEET_MOBILE_MAX_WIDTH_PX;
    const ref = useRef<HTMLDivElement>(null);
    const native: boolean = nativeKeyboard();
    const ios: boolean = isIOS();
    const viewportInset: number = useViewportInset();
    const nativeHeight: number = useNativeKeyboardHeight(native && active, () => {
        const focused: Element | null = document.activeElement;
        return !!focused && !!ref.current?.closest('[role="dialog"]')?.contains(focused);
    });
    const [preInset, setPreInset] = useState<number | null>(null);
    const preTimer = useRef<number | undefined>(undefined);
    useEffect(() => () => window.clearTimeout(preTimer.current), []);
    useEffect(() => {
        if (!active) setPreInset(null);
    }, [active]);
    const measured: number = native ? nativeHeight : viewportInset;
    useEffect(() => {
        if (measured <= 0) return;
        if (measured > SOFT_KEYBOARD_MIN_PX) rememberKeyboardInset(measured);
        window.clearTimeout(preTimer.current);
        setPreInset(null);
    }, [measured]);

    const touchStartY = useRef<number>(0);
    const onTouchStartCapture = useCallback((event: React.TouchEvent<HTMLDivElement>): void => {
        touchStartY.current = event.touches[0]?.clientY ?? 0;
    }, []);
    const onTouchEndCapture = useCallback((event: React.TouchEvent<HTMLDivElement>): void => {
        const field: HTMLInputElement | HTMLTextAreaElement | null = keyboardFieldOf(event.target);
        if (!ios || !active || !field || document.activeElement === field || !isMobileWidth(mobileMaxWidthPx)) return;
        const endY: number = event.changedTouches[0]?.clientY ?? touchStartY.current;
        if (Math.abs(endY - touchStartY.current) > 10) return;
        // 다른 칸에서 옮겨 오는 경우(키보드가 이미 떠 있음)는 iOS 에 맡긴다
        if (measured > 0) return;
        event.preventDefault();
        if (native) Keyboard.setResizeMode({ mode: KeyboardResize.None }).catch(() => undefined);
        window.clearTimeout(preTimer.current);
        flushSync(() => setPreInset(estimatedKeyboardInset()));
        preTimer.current = window.setTimeout(() => setPreInset(null), PRE_INSET_TIMEOUT_MS);
        field.focus({ preventScroll: true });
    }, [active, ios, measured, mobileMaxWidthPx, native]);

    // 앱을 나가면(다른 앱·홈) 시트 안 입력칸을 내린다 — 돌아올 때 키보드 위치 신호가 안 와 시트가 키보드 뒤로 숨는다(UI 가이드 7.1). 쓰던 글은 남는다
    useEffect(() => {
        if (!active) return undefined;
        const onHide = (): void => {
            const focused: Element | null = document.activeElement;
            if (document.visibilityState === 'hidden' && focused instanceof HTMLElement && ref.current?.closest('[role="dialog"]')?.contains(focused)) focused.blur();
        };
        document.addEventListener('visibilitychange', onHide);
        window.addEventListener('pagehide', onHide);
        return () => {
            document.removeEventListener('visibilitychange', onHide);
            window.removeEventListener('pagehide', onHide);
        };
    }, [active]);

    // 키보드가 떠 있는 동안 끌 곳이 없는 끌기는 막는다
    const keyboardShown: boolean = measured > 0 || preInset !== null;
    useEffect(() => {
        if (!ios || !active || !keyboardShown) return undefined;
        let lastX: number = 0;
        let lastY: number = 0;
        const onStart = (event: TouchEvent): void => {
            lastX = event.touches[0]?.clientX ?? 0;
            lastY = event.touches[0]?.clientY ?? 0;
        };
        const onMove = (event: TouchEvent): void => {
            if (event.touches.length !== 1 || !event.cancelable) return;
            const touch: Touch = event.touches[0];
            const dx: number = touch.clientX - lastX;
            const dy: number = touch.clientY - lastY;
            lastX = touch.clientX;
            lastY = touch.clientY;
            if (dy === 0 || Math.abs(dx) > Math.abs(dy)) return;
            for (let el: Element | null = event.target instanceof Element ? event.target : null; el && el !== document.body; el = el.parentElement) {
                const overflowY: string = window.getComputedStyle(el).overflowY;
                if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
                    const can: boolean = dy > 0 ? el.scrollTop > 0 : el.scrollTop < el.scrollHeight - el.clientHeight - 1;
                    if (can) return;
                }
            }
            event.preventDefault();
        };
        document.addEventListener('touchstart', onStart, { passive: true });
        document.addEventListener('touchmove', onMove, { passive: false });
        return () => {
            document.removeEventListener('touchstart', onStart);
            document.removeEventListener('touchmove', onMove);
        };
    }, [active, ios, keyboardShown]);

    const value: number | undefined = native ? (nativeHeight > 0 ? nativeHeight : preInset ?? undefined) : preInset ?? undefined;
    const inset: number = !ios || !active ? 0 : (native ? nativeHeight : viewportInset) || preInset || 0;

    // 창이 키보드 위로 줄어든 뒤 포커스된 칸이 창 안 스크롤 영역에서 가려져 있으면 그 영역만 스크롤해 보이게 한다(문서는 건드리지 않는다)
    useEffect(() => {
        if (inset <= 0) return undefined;
        const frame: number = window.requestAnimationFrame(() => {
            const focused: Element | null = document.activeElement;
            const dialog: Element | null | undefined = ref.current?.closest('[role="dialog"]');
            if (!(focused instanceof HTMLElement) || !dialog?.contains(focused)) return;
            for (let el: HTMLElement | null = focused.parentElement; el && dialog.contains(el); el = el.parentElement) {
                const overflowY: string = window.getComputedStyle(el).overflowY;
                if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
                    const box: DOMRect = el.getBoundingClientRect();
                    const rect: DOMRect = focused.getBoundingClientRect();
                    if (rect.bottom > box.bottom - 8) el.scrollTop += rect.bottom - box.bottom + 8;
                    else if (rect.top < box.top + 8) el.scrollTop -= box.top + 8 - rect.top;
                    break;
                }
            }
        });
        return () => window.cancelAnimationFrame(frame);
    }, [inset]);
    return { preInset: value, inset, containerProps: { ref, onTouchStartCapture, onTouchEndCapture } };
};
